/**
 * Token Storage Service
 * Manages access and refresh tokens securely
 * 
 * Security Strategy:
 * - Access tokens: Stored in memory (cleared on page refresh)
 * - Refresh tokens: Stored in sessionStorage (cleared when browser closes)
 * - Tokens are never in localStorage (XSS vulnerable)
 */

import { decodeJWT } from '@shared/utils/jwt.utils';
import { logger } from '@shared/utils/logger';

interface UserData {
  userId: number;
  name?: string;
  role: string;
  mobile?: string;
  permissions?: Record<string, boolean>;
}

// In-memory storage (most secure for access token)
let inMemoryAccessToken: string | null = null;
let inMemoryTokenExpiry: number | null = null;

const TOKEN_KEYS = {
  REFRESH_TOKEN: 'kaps_refresh_token', // sessionStorage only
  USER_DATA: 'kaps_user_data',
} as const;

export const tokenStorage = {
  /**
   * Store authentication tokens
   * Access token in memory, refresh token in sessionStorage
   */
  setTokens: (accessToken: string, refreshToken: string, expiryMillis: number) => {
    // Store access token in memory only
    inMemoryAccessToken = accessToken;
    inMemoryTokenExpiry = Date.now() + expiryMillis;
    
    // Store refresh token in sessionStorage (cleared when browser closes)
    sessionStorage.setItem(TOKEN_KEYS.REFRESH_TOKEN, refreshToken);
    
    // Decode and store user data
    tokenStorage.storeUserDataFromToken(accessToken);
  },

  /**
   * Get access token from memory
   */
  getAccessToken: (): string | null => {
    return inMemoryAccessToken;
  },

  /**
   * Get refresh token from sessionStorage
   */
  getRefreshToken: (): string | null => {
    return sessionStorage.getItem(TOKEN_KEYS.REFRESH_TOKEN);
  },

  /**
   * Get token expiry timestamp
   */
  getTokenExpiry: (): number | null => {
    return inMemoryTokenExpiry;
  },

  /**
   * Check if access token is expired or about to expire
   * @param bufferSeconds - Number of seconds before expiry to consider token as expired (default: 60)
   */
  isTokenExpired: (bufferSeconds: number = 60): boolean => {
    if (!inMemoryTokenExpiry) return true;
    
    const bufferMillis = bufferSeconds * 1000;
    return Date.now() >= (inMemoryTokenExpiry - bufferMillis);
  },

  /**
   * Update only the access token (used after refresh)
   */
  setAccessToken: (accessToken: string, expiryMillis: number) => {
    inMemoryAccessToken = accessToken;
    inMemoryTokenExpiry = Date.now() + expiryMillis;
    
    // Decode and store user data
    tokenStorage.storeUserDataFromToken(accessToken);
  },

  /**
   * Decode JWT and store user data
   */
  storeUserDataFromToken: (token: string): void => {
    const payload = decodeJWT(token);
    
    if (!payload) {
      logger.error('Failed to decode JWT token');
      return;
    }

    const userData: UserData = {
      userId: payload.userId ?? parseInt(payload.sub, 10),
      name: payload.name,
      role: payload.role,
      mobile: payload.mobile,
      permissions: payload.permissions,
    };

    sessionStorage.setItem(TOKEN_KEYS.USER_DATA, JSON.stringify(userData));
  },

  /**
   * Get stored user data
   */
  getUserData: (): UserData | null => {
    const data = sessionStorage.getItem(TOKEN_KEYS.USER_DATA);
    return data ? JSON.parse(data) : null;
  },

  /**
   * Check if user has specific permission
   */
  hasPermission: (permission: string): boolean => {
    const userData = tokenStorage.getUserData();
    if (!userData || !userData.permissions) return false;
    return userData.permissions[permission] === true;
  },

  /**
   * Clear all tokens (logout)
   */
  clearTokens: () => {
    // Clear in-memory tokens
    inMemoryAccessToken = null;
    inMemoryTokenExpiry = null;
    
    // Clear sessionStorage
    sessionStorage.removeItem(TOKEN_KEYS.REFRESH_TOKEN);
    sessionStorage.removeItem(TOKEN_KEYS.USER_DATA);
    
    // Also clear any old localStorage tokens (migration)
    localStorage.removeItem('kaps_access_token');
    localStorage.removeItem('kaps_refresh_token');
    localStorage.removeItem('kaps_token_expiry');
    localStorage.removeItem('kaps_user_data');
  },

  /**
   * Check if user has valid tokens
   */
  hasValidTokens: (): boolean => {
    return !!inMemoryAccessToken && !!tokenStorage.getRefreshToken();
  },

  /**
   * Check if tokens exist (for compatibility)
   */
  hasTokens: (): boolean => {
    return !!tokenStorage.getRefreshToken();
  },
};
